import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { StepProgress } from "../components/plan/StepProgress";
import { LoadingScreen } from "../components/plan/LoadingScreen";
import { Step1Trip } from "../components/plan/Step1Trip";
import { Step2Desires } from "../components/plan/Step2Desires";
import { Step3Preferences } from "../components/plan/Step3Preferences";
import { INITIAL, toggle } from "../components/plan/planConstants";

export default function PlanPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Ref pour éviter la double-exécution si les deps changent à step=4
  const generationStartedRef = useRef(false);

  const set = (key, val) => setData((d) => ({ ...d, [key]: val }));
  const tog = (key, val) => setData((d) => ({ ...d, [key]: toggle(d[key], val) }));
  const next = () => setStep((s) => s + 1);
  const prev = () => setStep((s) => s - 1);

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let res;
      for (let attempt = 1; attempt <= 5; attempt++) {
        try {
          const response = await axios.post(`${API}/generate-trip`, {
            ...data,
            budget: data.budget_montant
              ? `${data.budget_montant} € ${data.budget_type === "par_personne" ? "par personne" : "total"}${data.vols_inclus ? " (vols déjà réservés)" : ""}`
              : "",
          });
          res = response.data;
          break;
        } catch (err) {
          const status = err.response?.status;
          if ((status === 502 || status === 503 || !status) && attempt < 5) {
            await new Promise((r) => setTimeout(r, 3000 * attempt));
            continue;
          }
          throw err;
        }
      }

      const jobId = res.job_id;
      if (!jobId) throw new Error("Pas de job_id reçu");

      let attempts = 0;
      while (attempts < 60) {
        await new Promise((r) => setTimeout(r, 4000));
        attempts++;
        try {
          const { data: job } = await axios.get(`${API}/jobs/${jobId}`);
          if (job.status === "complete" && job.trip_id) {
            toast.success("Votre voyage a été créé !");
            navigate(`/trip/${job.trip_id}`);
            return;
          }
          if (job.status === "error") throw new Error(job.error || "Erreur lors de la génération");
        } catch (pollErr) {
          const pollStatus = pollErr.response?.status;
          if (pollStatus === 502 || pollStatus === 503 || !pollStatus) continue;
          throw pollErr;
        }
      }
      throw new Error("La génération a pris trop de temps. Veuillez réessayer.");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      const msg = typeof detail === "string" ? detail : "Erreur de génération. Veuillez réessayer.";
      setError(msg);
      toast.error(msg);
      setStep(3);
    } finally {
      setLoading(false);
    }
  }, [data, navigate]);

  useEffect(() => {
    if (step !== 4) { generationStartedRef.current = false; return; }
    if (generationStartedRef.current) return;
    generationStartedRef.current = true;
    handleGenerate();
  }, [step, handleGenerate]);

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />
      <StepProgress step={step < 4 ? step : 4} total={4} />
      <div className="max-w-2xl mx-auto px-5 sm:px-8 pb-16">
        {step === 1 && <Step1Trip data={data} set={set} onNext={next} />}
        {step === 2 && <Step2Desires data={data} set={set} tog={tog} onNext={next} onPrev={prev} />}
        {step === 3 && (
          <Step3Preferences
            data={data}
            set={set}
            tog={tog}
            error={error}
            onNext={() => { setError(""); setStep(4); }}
            onPrev={prev}
          />
        )}
        {step === 4 && <LoadingScreen />}
      </div>
    </div>
  );
}
